import { subject } from '@casl/ability';
import { IonFab, IonFabButton, IonIcon, IonPage } from '@ionic/react';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Can } from '../../api/authorization.provider';
import {
  Chat,
  ChatSource,
  useGetChatQuery,
  useGetTeamQuery,
} from '../../api/graphql/generated';
import { BookmarkType } from '../../api/hooks/bookmark.hooks';
import { useCurrentUserProp } from '../../api/user.hooks';
import { ConversationList } from '../../components/conversation/conversation-list';
import { ConversationModalProvider } from '../../components/conversation/conversation-modal-provider';
import { PageHeader } from '../../components/header/page.header';
import { newChat } from '../../components/icons/new-chat';
import { trackInitiateCreateChatMessage } from '../../config/analytics';
import {
  useKeyboardWillHide,
  useKeyboardWillShow,
} from '../../theme/keyboard.hook';
import { ChatNotificationToggleButton } from './chat-notification-toggle-button';

interface ChatPageProps {
  id: string;
  teamId: string;
}

const ChatPage = (props: ChatPageProps): ReactElement => {
  const { id, teamId } = props;
  const { i18n } = useTranslation();
  const [isKeyboardOpen, setKeyboardOpen] = useState<boolean>(false);
  const { data: chatData } = useGetChatQuery({
    variables: {
      input: {
        id,
      },
    },
  });

  const { data: teamData } = useGetTeamQuery({
    variables: { input: { id: teamId } },
  });

  useKeyboardWillShow(() => {
    setKeyboardOpen(true);
  });

  useKeyboardWillHide(() => {
    setKeyboardOpen(false);
  });

  const chat: Chat | undefined = chatData?.getChat;

  const title: string = i18n.t('common:chat');

  let description: string | undefined;

  if (teamData?.getTeam) {
    description = teamData.getTeam.description ?? undefined;
  }

  const userId = useCurrentUserProp('id');

  const chatMessageEditPermissionObj = {
    chatId: id,
    source: ChatSource.USER,
    sourceId: userId,
  };

  const headerTitle = chat?.name ? `${chat?.name} - ${title}` : title;

  return (
    <ConversationModalProvider id={id}>
      {({ containerRef, createNew }) => (
        <IonPage ref={containerRef}>
          <PageHeader
            title={headerTitle}
            subtitle={description}
            rightButtons={<ChatNotificationToggleButton chatId={id} />}
            bookmarkProps={{
              enable: true,
              type: BookmarkType.CHAT,
            }}
          />
          <ConversationList chatId={id} />
          {!isKeyboardOpen && (
            <Can
              I="create"
              this={subject('chat_message', chatMessageEditPermissionObj)}
            >
              <IonFab vertical="bottom" horizontal="end" slot="fixed">
                <IonFabButton
                  onClick={() => {
                    trackInitiateCreateChatMessage();
                    createNew();
                  }}
                >
                  <IonIcon icon={newChat} />
                </IonFabButton>
              </IonFab>
            </Can>
          )}
        </IonPage>
      )}
    </ConversationModalProvider>
  );
};

export { ChatPage };
